import { useEffect } from 'react';

const DEFAULT_TITLE = 'Whispering Wristwear · Handmade Morse code bracelets';
const DEFAULT_DESC =
  "Quiet messages worn close to the skin. Hand-strung in Lindy's Paphos studio.";
const DEFAULT_IMAGE = 'https://whisperingwristwear.com/images/lindy-in-studio.jpg';
const SITE_URL = 'https://whisperingwristwear.com';

/** Find or create a meta tag (or <link>) and update its content/href. */
function setHeadTag(selector, attrName, value, createTag) {
  if (typeof document === 'undefined') return;
  let el = document.head.querySelector(selector);
  if (!el && createTag) {
    el = createTag();
    document.head.appendChild(el);
  }
  if (el && value != null) el.setAttribute(attrName, value);
}

function upsertMeta(name, content, isProperty = false) {
  const attr = isProperty ? 'property' : 'name';
  setHeadTag(
    `meta[${attr}="${name}"]`,
    'content',
    content,
    () => {
      const m = document.createElement('meta');
      m.setAttribute(attr, name);
      return m;
    },
  );
}

function upsertCanonical(href) {
  setHeadTag(
    'link[rel="canonical"]',
    'href',
    href,
    () => {
      const l = document.createElement('link');
      l.setAttribute('rel', 'canonical');
      return l;
    },
  );
}

/** Per-page JSON-LD slot — single managed script tag we replace each route. */
function upsertJsonLd(payload) {
  if (typeof document === 'undefined') return;
  let el = document.head.querySelector('script[data-ww-page-ld]');
  if (!payload) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute('data-ww-page-ld', 'true');
    document.head.appendChild(el);
  }
  try {
    el.textContent = JSON.stringify(payload);
  } catch {
    /* ignore */
  }
}

/**
 * Per-page SEO meta. Updates document.title and the relevant <meta>/<link>
 * tags imperatively. Renders nothing visible.
 */
export default function Seo({
  title,
  description,
  image,
  path,
  type = 'website',
  jsonLd,
  noindex = false,
}) {
  useEffect(() => {
    const fullTitle = title ? `${title} · Whispering Wristwear` : DEFAULT_TITLE;
    const desc = description || DEFAULT_DESC;
    const img = image || DEFAULT_IMAGE;
    const url = path
      ? `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`
      : SITE_URL;

    document.title = fullTitle;
    upsertMeta('description', desc);
    upsertCanonical(url);

    upsertMeta('og:title', fullTitle, true);
    upsertMeta('og:description', desc, true);
    upsertMeta('og:url', url, true);
    upsertMeta('og:image', img, true);
    upsertMeta('og:type', type, true);

    upsertMeta('twitter:card', 'summary_large_image');
    upsertMeta('twitter:title', fullTitle);
    upsertMeta('twitter:description', desc);
    upsertMeta('twitter:image', img);

    upsertMeta('robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large');

    upsertJsonLd(jsonLd);
  }, [title, description, image, path, type, noindex, jsonLd]);

  return null;
}
